// =============================================================================
// Pure helpers for the organizational-unit hierarchy.
//
// Extracted from useOrganizationalStructure so the ordering and re-parenting
// rules can be tested without a Supabase client. Each one exists because of a
// specific defect in the admin company-profile screen — see the notes below.
// =============================================================================

/** Minimal shape these helpers need; the hook's OrganizationalUnit satisfies it. */
export interface TreeUnitLike {
  id: string;
  parent_id: string | null;
  sort_order?: number;
}

export interface TreeNode<T extends TreeUnitLike> {
  children: Array<T & { children: unknown[] }>;
}

/**
 * Build a forest from an adjacency list.
 *
 * A node whose parent is absent from `units` becomes a root. That fallback is
 * what made deleting a mid-level department promote its entire subtree to the top
 * of the chart, because the soft-deleted parent stopped being fetched while its
 * children still pointed at it — hence `reparentTarget` below.
 */
export function buildTree<T extends TreeUnitLike>(units: T[]): Array<T & { children: T[] }> {
  type Node = T & { children: T[] };
  const map = new Map<string, Node>();
  const roots: Node[] = [];

  for (const unit of units) {
    map.set(unit.id, { ...unit, children: [] } as Node);
  }

  for (const unit of units) {
    const node = map.get(unit.id)!;
    const parent = unit.parent_id ? map.get(unit.parent_id) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortChildren = (nodes: Node[]) => {
    nodes.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    for (const node of nodes) sortChildren(node.children as Node[]);
  };
  sortChildren(roots);

  return roots;
}

/**
 * Next free sort_order among a parent's children.
 *
 * Everything created through the UI used to be inserted with sort_order 0, so
 * siblings had no defined order and the chart reshuffled between loads.
 */
export function nextSortOrder(units: TreeUnitLike[], parentId: string | null | undefined): number {
  const siblings = units.filter((u) => (u.parent_id ?? null) === (parentId ?? null));
  if (siblings.length === 0) return 0;
  return Math.max(...siblings.map((u) => u.sort_order ?? 0)) + 1;
}

/** Total number of units below `unitId`, at any depth. */
export function countDescendants(units: TreeUnitLike[], unitId: string): number {
  let total = 0;
  const walk = (id: string) => {
    for (const u of units) {
      if (u.parent_id === id) {
        total += 1;
        walk(u.id);
      }
    }
  };
  walk(unitId);
  return total;
}

/**
 * Where the direct children of a unit being deleted should be re-attached:
 * the deleted unit's own parent, or the root when it had none.
 */
export function reparentTarget(units: TreeUnitLike[], unitId: string): string | null {
  return units.find((u) => u.id === unitId)?.parent_id ?? null;
}
